import { createEffect, createSignal, untrack } from 'solid-js';
import { COVERED, fundOf } from '~data';
import { detail, setDetail } from '~/stores/nav-store';

// Which funds the compare page is holding side by side, and what colour each one
// is while it is there.
//
// Kept in the pool's own order rather than the order you clicked them, so the
// columns do not reshuffle as you add one — a table whose columns move while you
// are reading across it is worse than one that makes you find the fund yourself.

export const POOL = COVERED.map((e) => e.symbol);

/**
 * Eight hues, and a second channel so the ninth fund is not an error message.
 *
 * The hues are never cycled, which is not a stylistic rule — past about eight,
 * added hues stop being tellable apart, and stop much sooner than that for the
 * one reader in twelve with a colour vision deficiency. A generated ninth colour
 * does not produce a ninth identity; it produces two funds that look the same
 * and a chart that quietly lies about which line is which.
 *
 * So the ninth fund takes hue one again and a dashed line. Identity becomes the
 * pair, and a pair is unambiguous where a hue alone would not be. This is the
 * standard answer for more series than hues, and it has the useful property of
 * degrading well: it still reads in greyscale, in print, and under any flavour
 * of colour blindness, none of which is true of a bigger palette.
 *
 * Slots are handed out lowest-first, so the first eight funds — the case that
 * actually happens — are all solid and all maximally separated. The dashes only
 * appear once someone has asked for more than a chart can really carry.
 */
const HUES = 8;

/** Solid, dashed, dotted, dash-dot. Lengths are in screen pixels because the
    paths are drawn with a non-scaling stroke. */
const DASHES: (string | undefined)[] = [undefined, '8 4', '1.5 3', '7 3 1.5 3'];

/**
 * Thirty-two, against a pool of eighteen — so running out is unreachable, and
 * there is no "no colour left" state to report.
 *
 * If coverage ever outgrows this, the answer is not a fifth dash pattern. Thirty
 * overlaid lines are unreadable however they are encoded, and the page already
 * has the right tool for that: the Separate view, which is small multiples.
 */
export const SERIES_LIMIT = HUES * DASHES.length;

const [selected, setSelectedSignal] = createSignal<string[]>([]);

/**
 * Which palette slot each selected fund holds.
 *
 * A fund keeps its slot for as long as it stays selected, and a slot is only
 * freed by deselecting the fund that held it. This is the whole point: colour
 * has to follow the fund, not its position in the list. Colouring by index into
 * the selection meant removing an early fund shifted every later one down a hue
 * — someone who had learned "AMAX is the orange one" was then reading the wrong
 * line, which is worse than no colour at all.
 *
 * Slots are handed out lowest-free-first, so the palette is used from the top
 * and the common two- or three-fund case always gets the best-separated hues.
 */
const [slotOf, setSlotOf] = createSignal<Record<string, number>>({});

export { selected };

export const isSelected = (symbol: string): boolean => selected().includes(symbol);

const firstFreeSlot = (taken: Record<string, number>): number => {
    const used = new Set(Object.values(taken));
    for (let slot = 0; slot < SERIES_LIMIT; slot += 1) if (!used.has(slot)) return slot;
    return -1;
};

const select = (symbol: string): void => {
    setSlotOf((prev) => {
        if (symbol in prev) return prev;
        const slot = firstFreeSlot(prev);
        // Every slot taken: the fund is still selected and still gets its column
        // in the table, it just cannot be drawn on the overlay. Reported there
        // rather than silently missing.
        return slot < 0 ? prev : { ...prev, [symbol]: slot };
    });
    setSelectedSignal((current) => POOL.filter((s) => s === symbol || current.includes(s)));
};

const deselect = (symbol: string): void => {
    setSlotOf((prev) => {
        const next = { ...prev };
        delete next[symbol];
        return next;
    });
    setSelectedSignal((current) => current.filter((s) => s !== symbol));
};

export const toggle = (symbol: string): void => {
    if (isSelected(symbol)) deselect(symbol);
    else select(symbol);
};

export const clear = (): void => {
    setSelectedSignal([]);
    setSlotOf({});
};

export interface SeriesStyle {
    color: string;
    /** SVG stroke-dasharray, absent for a solid line. */
    dash?: string;
}

/** How this fund is drawn: hue from the slot, line pattern from how many times
    the palette has been round. */
export const styleOf = (symbol: string): SeriesStyle | null => {
    const slot = slotOf()[symbol];
    if (slot == null) return null;
    return { color: `var(--series-${(slot % HUES) + 1})`, dash: DASHES[Math.floor(slot / HUES)] };
};

/** Selected funds, in pool order — what the overlay draws. Every selected fund
    is here: the slot supply exceeds the pool, so nothing is left undrawn. */
export const drawn = (): (SeriesStyle & { symbol: string })[] =>
    selected()
        .filter((s) => slotOf()[s] != null)
        .map((s) => ({ symbol: s, ...styleOf(s)! }));

/**
 * The opening selection, from the address bar where there is one.
 *
 * Comma-separated, in the hash's second segment — `#/compare/HMAX,HTA,HYLD`. A
 * comparison is the whole state of this page, so a reload that dropped it back
 * to three arbitrary funds would throw away the only thing you were doing here.
 *
 * Unknown symbols are ignored rather than failing the whole list: a link written
 * before a fund was renamed should still bring back the funds it names that
 * still exist.
 */
const opening = (): string[] => {
    const named = (detail() ?? '').split(',').filter((s) => s !== '' && fundOf(s) != null);
    return named.length > 0 ? named : POOL.slice(0, 3);
};

opening().forEach(select);

/**
 * Keep the selection and the address bar in step, in both directions.
 *
 * Called from the app root rather than run at module scope. An effect created
 * outside a render has no owner and is not flushed until something else forces
 * the queue, which showed up exactly as reported: the first add did nothing to
 * the URL and felt like a lag, because that click was what finally ran the
 * effect that should already have been live.
 */
export const syncUrl = (): void => {
    // Pool order rather than click order, so the same three funds always produce
    // the same link however you arrived at them.
    createEffect(() => setDetail(selected().length > 0 ? selected().join(',') : null));

    // Tracks the hash and nothing else. Reading `selected()` here made this
    // effect re-run on every add, comparing the new selection against the URL
    // that had not been written yet, and putting the old selection back — which
    // looked like the URL never changing and the click fighting you.
    createEffect(() => {
        const named = (detail() ?? '').split(',').filter((s) => s !== '' && fundOf(s) != null);
        if (named.length === 0 || named.join(',') === untrack(selected).join(',')) return;
        untrack(() => {
            clear();
            named.forEach(select);
        });
    });
};
